import React, { useEffect, useMemo, useRef, useState } from "react";

import { strings } from "@perceptron/constants";
import { AnnotationSource } from "@perceptron/core/types";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { isNumber, isString } from "lodash-es";

import { useStreamSource } from "@demo/contexts/StreamSourceContext";
import {
  isVideoAnnotation,
  isVideoFocusAnnotation,
  useAnnotationInteractions,
} from "@demo/hooks/annotations/useAnnotationInteractions";
import { getAnnotationKey } from "@demo/utils/annotations";

/**
 * Post-order React-children walk that returns descendant annotation keys.
 * Image collections highlight leaves; video collections also include nested containers.
 * A node is treated as a container whenever it has a numbered descendant —
 * so collections, tracks, and any future wrapper behave uniformly without
 * hard-coding component types.
 */
const collectAnnotationKeys = (
  node: React.ReactNode,
  source: AnnotationSource,
  includeContainers = false,
  acc: string[] = [],
): string[] => {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as { number?: unknown; children?: React.ReactNode };

    const before = acc.length;
    collectAnnotationKeys(props.children, source, includeContainers, acc);
    const foundDescendants = acc.length > before;

    if ((!foundDescendants || includeContainers) && isNumber(props.number)) {
      acc.push(getAnnotationKey(props.number, source));
    }
  });
  return acc;
};

export interface CollectionAnnotationTextProps {
  children?: React.ReactNode;
}

const CollectionAnnotationText: React.FC<CollectionAnnotationTextProps & { [key: string]: unknown }> = ({
  children,
  ...props
}) => {
  const source = useStreamSource();
  const {
    handleMouseEnter,
    handleMouseLeave,
    findAnnotationById,
    setHighlightedAnnotationIds,
    isKeyHighlighted,
    isKeyClicked,
  } = useAnnotationInteractions();

  const isStreaming = props["data-streaming"] === "true";
  const number = props.number;
  const mention = props.mention;
  const collectionKey = isNumber(number) ? getAnnotationKey(number, source) : "";
  const annotation = findAnnotationById(collectionKey);
  const isVideoCollection = isVideoAnnotation(annotation);
  const canFocusVideoCollection = isVideoFocusAnnotation(annotation);

  const [isOpen, setIsOpen] = useState(false);

  const childrenRefs = useRef<HTMLSpanElement[]>([]);

  const collectionPoints = useMemo(() => {
    return Array.isArray(children) ? children : [];
  }, [children]);

  const pointKeys = useMemo(
    () => collectAnnotationKeys(children, source, isVideoCollection),
    [children, source, isVideoCollection],
  );
  const selectionKeys = canFocusVideoCollection ? [collectionKey, ...pointKeys] : isVideoCollection ? [] : pointKeys;

  // Scope-aware so the chip lights up for any descendant's highlight —
  // including cluster-scoped hovers that dispatch `${trackKey}::c::${idx}`.
  const isCollectionPointClicked = isKeyClicked(pointKeys);
  const isCollectionPointHighlighted = isKeyHighlighted(selectionKeys);

  const toggleCollection = (open: boolean) => {
    if (!open) {
      childrenRefs.current.reverse().forEach((child, index) => {
        setTimeout(() => {
          child.classList.add("pointer-events-none");
          child.classList.remove("inline");
          child.classList.remove("max-h-[5rem]");
          child.classList.remove("max-w-[1000px]");
          child.classList.remove("translate-x-0");
          child.classList.remove("opacity-100");
        }, index * delayPerChild);
      });
    } else {
      childrenRefs.current.forEach((child, index) => {
        setTimeout(() => {
          child.classList.remove("pointer-events-none");
          child.classList.add("inline");
          child.classList.add("max-h-[5rem]");
          child.classList.add("max-w-[1000px]");
          child.classList.add("translate-x-0");
          child.classList.add("opacity-100");
        }, index * delayPerChild);
      });
    }
  };

  useEffect(() => {
    if (isOpen) return;

    if (isCollectionPointClicked) {
      setIsOpen(true);
      toggleCollection(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollectionPointClicked]);

  // Streaming holds the collection open so members are visible as they arrive,
  // and the end of the stream collapses them again. The toggle has to follow
  // that collapse: a collection opened mid-stream — by clicking one of its
  // members, say — would otherwise keep its chevron pointing open over content
  // that has already closed, which reads as half-selected.
  //
  // Only the moment streaming ends closes it. A collection that is simply not
  // streaming is free to be open, which is how an auto-open on a pinned member
  // stays open.
  const wasStreaming = useRef(isStreaming);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming) setIsOpen(false);
    wasStreaming.current = isStreaming;
  }, [isStreaming]);

  if (!isNumber(number)) return null;

  const labelNumber = props["data-label-number"];
  const label = isString(mention) ? mention : `Collection ${isNumber(labelNumber) ? labelNumber : number}`;
  const delayPerChild = 350 / collectionPoints.length;
  const activateCollection = () => {
    setIsOpen(!isOpen);
    toggleCollection(!isOpen);
  };

  return (
    <>
      <span
        aria-label={strings.annotations.ariaLabels.toggleCollection}
        className={cn(
          "group relative z-10 inline cursor-pointer text-white underline decoration-white decoration-dotted decoration-1 underline-offset-2 transition-[padding]",
          {
            "collection-marker-streaming": isStreaming,
            "decoration-solid": isCollectionPointHighlighted,
          },
        )}
        data-video-annotation-control={isVideoCollection || undefined}
        role="button"
        tabIndex={0}
        onClick={activateCollection}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            activateCollection();
          }
        }}
        onMouseEnter={() => {
          if (isVideoCollection && !canFocusVideoCollection) return;
          handleMouseEnter(canFocusVideoCollection ? [collectionKey] : pointKeys, source);
        }}
        onMouseLeave={() => {
          if (isVideoCollection && !canFocusVideoCollection) return;
          handleMouseLeave(collectionKey);
          // An image collection highlights its members rather than itself, so
          // the hook's own clear — which spares a clicked id — can leave them
          // lit. Clear them directly.
          if (!canFocusVideoCollection) setHighlightedAnnotationIds(undefined, undefined);
        }}
      >
        {label}
      </span>
      <Icon
        className={cn("inline size-4 transition-[max-width,opacity,transform] duration-300 ease-in-out", {
          "max-w-[1000px] -rotate-90 opacity-100": isOpen || isStreaming,
          "max-w-0 -rotate-180 opacity-0": !isOpen && !isStreaming,
        })}
        data-testid="collection-chevron-down"
        name="chevron-down"
      />
      {collectionPoints?.map((child, index) => {
        return (
          <span
            className={cn(
              "z-0 origin-left overflow-hidden align-bottom transition-[margin,opacity,transform,max-width,max-height] duration-300 ease-in-out",
              "pointer-events-none inline-block max-h-0 max-w-0 opacity-0",
              {
                "inline max-h-[5rem] max-w-[1000px] translate-x-0 opacity-100": isStreaming,
              },
            )}
            key={index}
            ref={(el) => {
              if (!el) return;
              childrenRefs.current[index] = el;
            }}
          >
            {child}
          </span>
        );
      })}
    </>
  );
};

export default CollectionAnnotationText;
